import { MarkdownContent } from '@/components/shared/MarkdownContent';
import { formatReleaseNoteStatus } from '@/config/objects/release-notes';
import type { ReleaseNoteView, ReleaseNoteWithViews } from '@/config/objects/release-notes';
import { useFeatureTour } from '@/hooks/useFeatureTour';
import {
  Breadcrumbs,
  Button,
  DetailPageHeader,
  DetailPageHero,
  EmptyTableState,
  Loading,
  PageContainer,
  formatDate,
  formatDateTime,
  useApiClient,
} from '@startupquickstart/frontend';
import { useQuery } from '@tanstack/react-query';
import { Calendar, ListChecks, Megaphone, PlayCircle, Users } from 'lucide-react';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

/** Format a viewer record into a readable display name. */
function viewerName(user: ReleaseNoteView['user']): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name || user.email;
}

/** Release note detail page. Read-only, internal-only. */
export function ReleaseNoteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const apiClient = useApiClient();
  const { runFeatureTour } = useFeatureTour();

  const { data, isLoading } = useQuery<{ success: boolean; data: ReleaseNoteWithViews }>({
    queryKey: ['ReleaseNote', id],
    queryFn: () => apiClient.get(`/release-notes/${id}`),
    enabled: !!id,
  });

  const note = data?.data;
  const steps = note?.tutorialSteps ?? [];
  const views = note?.views ?? [];

  const handlePreviewTutorial = () => {
    void runFeatureTour(steps);
  };

  const breadcrumbs = useMemo(
    () => [
      { label: 'Home', href: '/' },
      { label: 'Release Notes', href: '/release-notes' },
      { label: note?.title ?? 'Release Note', current: true },
    ],
    [note?.title],
  );

  if (!id) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-lg font-medium mb-2">Release Note Not Found</h2>
          <p className="text-muted-foreground">
            The requested release note could not be found.
          </p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer padding="none" className="flex flex-col min-h-full">
      <DetailPageHeader>
        <Breadcrumbs items={breadcrumbs} />
      </DetailPageHeader>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loading />
        </div>
      ) : (
        <DetailPageHero
          isLoading={false}
          category={{ label: 'Release Note', icon: Megaphone }}
          title={note?.title ?? 'Release Note'}
          meta={
            <>
              {note?.publishedAt && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  Published {formatDate(note.publishedAt)}
                </span>
              )}
            </>
          }
          badges={note && formatReleaseNoteStatus(note.status)}
        >
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left — content */}
            <div className="lg:col-span-3 space-y-6">
              {note?.summary && (
                <div className="rounded-xl border bg-card shadow-sm">
                  <div className="px-6 py-4 border-b">
                    <h2 className="text-lg font-medium tracking-tight text-zinc-900">
                      Summary
                    </h2>
                  </div>
                  <div className="p-6">
                    <p className="text-sm text-foreground leading-relaxed">{note.summary}</p>
                  </div>
                </div>
              )}

              <div className="rounded-xl border bg-card shadow-sm">
                <div className="px-6 py-4 border-b">
                  <h2 className="text-lg font-medium tracking-tight text-zinc-900">Details</h2>
                </div>
                <div className="p-6">
                  {note?.body?.trim() ? (
                    <MarkdownContent content={note.body} />
                  ) : (
                    <EmptyTableState
                      icon={<Megaphone className="h-6 w-6" />}
                      title="No details yet"
                      description="This release note has no body content."
                    />
                  )}
                </div>
              </div>

              {/* Guided tour */}
              <div className="rounded-xl border bg-card shadow-sm">
                <div className="px-6 py-4 border-b flex items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-medium tracking-tight text-zinc-900">
                      Guided tour
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      <ListChecks className="h-3.5 w-3.5 inline mr-1" />
                      Steps highlighted for this release.
                    </p>
                  </div>
                  {steps.length > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      icon={PlayCircle}
                      onClick={handlePreviewTutorial}
                    >
                      Preview tutorial
                    </Button>
                  )}
                </div>
                <div className="p-6">
                  {steps.length === 0 ? (
                    <EmptyTableState
                      icon={<ListChecks className="h-6 w-6" />}
                      title="No tour steps"
                      description="This release note has no guided tour."
                    />
                  ) : (
                    <ol className="divide-y">
                      {steps.map((step, index) => (
                        <li key={index} className="py-3 first:pt-0">
                          <div className="flex items-baseline gap-3">
                            <span className="text-xs font-medium text-muted-foreground">
                              {index + 1}
                            </span>
                            <div>
                              <div className="text-sm font-medium text-zinc-900">
                                {step.title}
                              </div>
                              {step.description && (
                                <p className="text-sm text-muted-foreground">
                                  {step.description}
                                </p>
                              )}
                              <code className="text-xs text-muted-foreground">
                                {step.selector}
                                {step.route ? ` · ${step.route}` : ''}
                              </code>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              </div>
            </div>

            {/* Right — viewers */}
            <div className="lg:col-span-2">
              <div className="rounded-xl border bg-card shadow-sm">
                <div className="px-6 py-4 border-b">
                  <h2 className="text-lg font-medium tracking-tight text-zinc-900">
                    <Users className="h-4 w-4 inline mr-1.5 -mt-0.5" />
                    Viewers
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Who has seen this in the What’s New panel.
                  </p>
                </div>
                <div className="p-6">
                  {views.length === 0 ? (
                    <EmptyTableState
                      icon={<Users className="h-6 w-6" />}
                      title="No views yet"
                      description="No one has opened this release note."
                    />
                  ) : (
                    <ul className="divide-y">
                      {views.map((view) => (
                        <li key={view.id} className="py-3 first:pt-0">
                          <div className="text-sm font-medium text-zinc-900">
                            {viewerName(view.user)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {view.user.email}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {formatDateTime(view.viewedAt)}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        </DetailPageHero>
      )}
    </PageContainer>
  );
}
